import React, { useState, useEffect } from "react";
import api from "@/api";
import useModalEscape from "@/hooks/useModalEscape";
import TickerSearch from "@/components/TickerSearch";

const CreateAlertDialog = ({
  isOpen,
  onClose,
  ticker: initialTicker = "",
  currentPrice: initialCurrentPrice = null,
  onAlertCreated,
}) => {
  const [ticker, setTicker] = useState(initialTicker);
  const [currentPrice, setCurrentPrice] = useState(initialCurrentPrice);
  const [alertType, setAlertType] = useState("PRICE_ABOVE");
  const [targetPrice, setTargetPrice] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Close modal with Escape (TickerSearch stops Escape from bubbling when its
  // dropdown is open, so this won't close the modal mid-autocomplete).
  useModalEscape(onClose);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTicker(initialTicker || "");
      setCurrentPrice(initialCurrentPrice || null);
      setAlertType("PRICE_ABOVE");
      setTargetPrice("");
      setIsRecurring(false);
      setError(null);
    } else {
      // When opening with initialTicker, set currentPrice
      setCurrentPrice(initialCurrentPrice);
    }
  }, [isOpen, initialTicker, initialCurrentPrice]);

  // Handle ticker selection from the autocomplete dropdown
  const handleSelectTicker = (result) => {
    setTicker(result.ticker);
    setCurrentPrice(result.current_price);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!ticker || ticker.trim() === "") {
      setError("Please enter a ticker symbol");
      return;
    }

    const price = parseFloat(targetPrice);
    if (isNaN(price) || price <= 0) {
      setError("Please enter a valid price");
      return;
    }

    // Validate that the alert makes sense given current price (if available)
    if (currentPrice !== null) {
      if (alertType === "PRICE_ABOVE" && price <= currentPrice) {
        setError(`Target price must be above current price ($${currentPrice})`);
        return;
      }
      if (alertType === "PRICE_BELOW" && price >= currentPrice) {
        setError(`Target price must be below current price ($${currentPrice})`);
        return;
      }
    }

    try {
      setLoading(true);
      const response = await api.post("/alerts", {
        ticker: ticker.toUpperCase().trim(),
        alert_type: alertType,
        target_price: price,
        one_time: !isRecurring, // Note: Backend support for recurring alerts may need to be added
      });

      // Notify parent
      if (onAlertCreated) {
        onAlertCreated(response.data);
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error creating alert:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to create alert",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Set Price Alert
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              {/* Ticker Input with Autocomplete */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ticker Symbol
                </label>
                <TickerSearch
                  value={ticker}
                  onChange={setTicker}
                  onSelect={handleSelectTicker}
                  disabled={!!initialTicker}
                  placeholder="e.g., AAPL"
                />

                {/* Current Price Display */}
                {currentPrice !== null && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Current price: ${currentPrice.toFixed(2)}
                  </div>
                )}
              </div>

              {/* Alert Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Alert Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setAlertType("PRICE_ABOVE")}
                    className={`px-4 py-3 rounded-lg border-2 transition-all ${
                      alertType === "PRICE_ABOVE"
                        ? "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                        : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500"
                    }`}
                  >
                    <div className="text-sm font-medium">Price Above</div>
                    <div className="text-xs mt-1 opacity-75">
                      Alert when price rises
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAlertType("PRICE_BELOW")}
                    className={`px-4 py-3 rounded-lg border-2 transition-all ${
                      alertType === "PRICE_BELOW"
                        ? "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                        : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500"
                    }`}
                  >
                    <div className="text-sm font-medium">Price Below</div>
                    <div className="text-xs mt-1 opacity-75">
                      Alert when price drops
                    </div>
                  </button>
                </div>
              </div>

              {/* Target Price */}
              <div>
                <label
                  htmlFor="targetPrice"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Target Price
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                    $
                  </span>
                  <input
                    id="targetPrice"
                    type="number"
                    step="0.01"
                    value={targetPrice}
                    onChange={(e) => setTargetPrice(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-7 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {alertType === "PRICE_ABOVE"
                    ? `You'll be notified when ${ticker} rises above this price`
                    : `You'll be notified when ${ticker} falls below this price`}
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-700 dark:text-red-400">
                    {error}
                  </p>
                </div>
              )}

              {/* Recurring Alert Checkbox */}
              <div className="flex items-start gap-3">
                <input
                  id="isRecurring"
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label
                  htmlFor="isRecurring"
                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer font-medium"
                >
                  Keep alert active after triggering
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                )}
                {loading ? "Creating..." : "Create Alert"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateAlertDialog;
